import React from 'react';
import { PercentIcon, IndianRupeeIcon } from 'lucide-react';
import type { DiscountType } from '../../types/services';

interface DiscountInputProps {
  discountType: DiscountType | null | undefined;
  discountValue: number | undefined;
  discountReason?: string | null;
  maxAmount?: number;
  onDiscountTypeChange: (type: DiscountType | null) => void;
  onDiscountValueChange: (value: number | undefined) => void;
  onDiscountReasonChange?: (reason: string | null) => void;
  showReason?: boolean;
  compact?: boolean;
}

export function DiscountInput({
  discountType,
  discountValue,
  discountReason,
  maxAmount,
  onDiscountTypeChange,
  onDiscountValueChange,
  onDiscountReasonChange,
  showReason = false,
  compact = false,
}: DiscountInputProps) {
  const handleTypeToggle = (type: DiscountType) => {
    if (discountType === type) {
      onDiscountTypeChange(null);
      onDiscountValueChange(undefined);
    } else {
      onDiscountTypeChange(type);
    }
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === '') {
      onDiscountValueChange(undefined);
    } else {
      let numVal = Number(val);
      // For percentage, cap at 100
      if (discountType === 'percentage' && numVal > 100) {
        numVal = 100;
      }
      // For fixed, cap at maxAmount if provided
      if (discountType === 'fixed' && maxAmount !== undefined && numVal > maxAmount) {
        numVal = maxAmount;
      }
      onDiscountValueChange(numVal);
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
          <button
            type="button"
            onClick={() => handleTypeToggle('percentage')}
            className={`px-2 py-1.5 text-xs font-medium transition-colors ${
              discountType === 'percentage'
                ? 'bg-[#5B8E87] text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <PercentIcon className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => handleTypeToggle('fixed')}
            className={`px-2 py-1.5 text-xs font-medium transition-colors border-l border-gray-300 ${
              discountType === 'fixed'
                ? 'bg-[#5B8E87] text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <IndianRupeeIcon className="w-3 h-3" />
          </button>
        </div>
        {discountType && (
          <input
            type="number"
            value={discountValue ?? ''}
            onChange={handleValueChange}
            placeholder="0"
            min="0"
            max={discountType === 'percentage' ? 100 : undefined}
            className="w-16 px-2 py-1.5 text-sm rounded-lg border border-gray-300 focus:border-[#5B8E87] focus:ring-1 focus:ring-[#5B8E87] outline-none"
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <div className="flex rounded-xl border border-gray-300 overflow-hidden">
          <button
            type="button"
            onClick={() => handleTypeToggle('percentage')}
            className={`px-3 py-2 text-sm font-medium transition-colors flex items-center gap-1 ${
              discountType === 'percentage'
                ? 'bg-[#5B8E87] text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <PercentIcon className="w-4 h-4" />
            <span>%</span>
          </button>
          <button
            type="button"
            onClick={() => handleTypeToggle('fixed')}
            className={`px-3 py-2 text-sm font-medium transition-colors flex items-center gap-1 border-l border-gray-300 ${
              discountType === 'fixed'
                ? 'bg-[#5B8E87] text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <IndianRupeeIcon className="w-4 h-4" />
            <span>₹</span>
          </button>
        </div>
        {discountType && (
          <div className="flex-1">
            <input
              type="number"
              value={discountValue ?? ''}
              onChange={handleValueChange}
              placeholder={discountType === 'percentage' ? 'Enter %' : 'Enter amount'}
              min="0"
              max={discountType === 'percentage' ? 100 : undefined}
              className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
            />
          </div>
        )}
      </div>
      {showReason && discountType && onDiscountReasonChange && (
        <input
          type="text"
          value={discountReason || ''}
          onChange={(e) => onDiscountReasonChange(e.target.value || null)}
          placeholder="Discount reason (optional)"
          className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all text-sm"
        />
      )}
    </div>
  );
}
